import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Spinner } from "./PageLoader";
import { toast } from "react-toastify";
import config from "./config";
import LocationPicker from "./LocationPicker";
import PhoneField from "./PhoneField";
import { DEFAULT_ABOUT } from "./constants";

// A "blank" banner is the placeholder asset — shown grayscale like the community
// hero so default headers read as monochrome rather than a flat colour block.
const isEmptyBanner = (url) =>
  !url || /\/(signin-empty|logo-empty)\.png(\?|$)/i.test(url);

/**
 * Member profile modal — view stats + edit name/phone.
 */
function ProfileModal({ token, member, groupToken, primary = "#0274F7", banner, onClose, onUpdated, onPointsChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [about, setAbout] = useState("");
  const [loc, setLoc] = useState({ location: "", country: "", state: "", city: "", latitude: null, longitude: null });
  const [privacy, setPrivacy] = useState("city");
  const [img, setImg] = useState(member?.img || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-member-profile`, {
          community_token: token,
          community_member_id: member?.community_member_id,
        });
        if (!active) return;
        if (res.data?.error === 0) {
          const m = res.data.member;
          setData(m);
          setName(m.name || "");
          setPhone(m.phone || "");
          setImg(m.img || "");
          setAbout(m.about || DEFAULT_ABOUT);
          setPrivacy(m.location_privacy || "city");
          setLoc({
            location: m.location || "",
            country: m.country || "",
            state: m.state || "",
            city: m.city || "",
            latitude: m.latitude ?? null,
            longitude: m.longitude ?? null,
          });
        } else {
          toast.error(res.data?.msg || "Could not load profile.");
        }
      } catch (err) {
        if (active) toast.error("Could not load profile.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token, member]);

  // Lock the background page scroll while the modal is open (so the only
  // scrollable surface is the modal itself, and never the map behind it).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-member-update`, {
        community_token: token,
        community_member_id: member?.community_member_id,
        member_token: member?.auth_token,
        name,
        phone,
        about,
        location: loc.location,
        country: loc.country,
        state: loc.state,
        city: loc.city,
        latitude: loc.latitude,
        longitude: loc.longitude,
        location_privacy: privacy,
      });
      if (res.data?.error === 0) {
        toast.success("Profile updated.");
        setData((d) => ({ ...d, name, phone, about, ...loc, location_privacy: privacy }));
        onUpdated?.({ ...member, name });
        onClose?.(); // close the popup automatically after a successful save
      } else {
        toast.error(res.data?.msg || "Could not update.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("community_token", token);
      fd.append("community_member_id", member?.community_member_id);
      fd.append("member_token", member?.auth_token);
      if (groupToken) fd.append("group_token", groupToken);
      fd.append("avatar", file);
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-member-avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.error === 0) {
        // cache-bust so the new image shows immediately
        const url = `${res.data.img}?t=${Date.now()}`;
        setImg(url);
        // Reflect any profile-picture points awarded on first upload right away.
        if (typeof res.data.points === "number") {
          setData((d) => ({ ...d, points: res.data.points }));
        }
        toast.success("Photo updated.");
        onUpdated?.({ ...member, img: url });
        onPointsChange?.();
      } else {
        toast.error(res.data?.msg || "Could not upload photo.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Could not upload photo.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const initial = (name || member?.name || "?").charAt(0).toUpperCase();

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-stretch sm:items-center justify-center bg-gray-900/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        className="bg-white w-full h-full max-h-none rounded-none sm:h-auto sm:max-h-[94vh] sm:max-w-2xl sm:rounded-3xl shadow-2xl ring-1 ring-black/5 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile: slim app-style top bar (the gradient banner is desktop-only,
            so the form + location map get the full height without a cramped
            scroll). */}
        <div className="sm:hidden shrink-0 flex items-center justify-between border-b border-gray-100 px-3 py-3">
          <button onClick={onClose} aria-label="Close" className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          </button>
          <h3 className="text-base font-bold text-gray-900">Edit Profile</h3>
          <span className="w-9" />
        </div>

        {/* Branded header: community banner in the background, tinted with the
            selected theme colour so the text on top stays readable. */}
        <div className="relative hidden sm:block shrink-0 px-6 pt-7 pb-6 sm:pt-8 sm:pb-7 text-center overflow-hidden bg-gray-900">
          {banner && (
            <div
              aria-hidden="true"
              className={`absolute inset-0 z-0 bg-center bg-cover${
                isEmptyBanner(banner) ? " grayscale" : ""
              }`}
              style={{ backgroundImage: `url("${banner}")` }}
            />
          )}
          {/* Neutral black overlay over the banner — a soft dark tint, deeper at
              the bottom so the text stays legible (no brand tint). */}
          <div
            aria-hidden="true"
            className="absolute inset-0 z-0"
            style={{
              background: "linear-gradient(160deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.70) 100%)",
            }}
          />
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3.5 right-3.5 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 text-white text-lg leading-none transition z-10"
          >
            ×
          </button>
          <div className="relative z-10 w-24 h-24 mx-auto">
            <div className="w-24 h-24 rounded-3xl bg-white shadow-lg ring-4 ring-white/30 overflow-hidden flex items-center justify-center text-3xl font-bold" style={{ color: primary }}>
              {img ? (
                <img src={img} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                initial
              )}
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              aria-label="Change photo"
              className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-white shadow-md border flex items-center justify-center hover:bg-gray-50 disabled:opacity-60"
              style={{ color: primary }}
            >
              {uploading ? (
                <Spinner size={16} />
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 8h.01M4 16l4-4a3 3 0 014 0l4 4M14 14l1-1a3 3 0 014 0l1 1M4 6h4l1.5-2h5L16 6h4a1 1 0 011 1v11a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1z" />
                </svg>
              )}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => uploadAvatar(e.target.files?.[0])}
            />
          </div>
          <h3 className="text-white text-xl sm:text-2xl font-bold mt-4 sm:mt-5 relative z-10" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>{name || member?.name}</h3>
          <p className="text-white/95 text-sm mt-1 relative z-10" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>{data?.email || member?.email}</p>
          {data?.joined_on && (
            <span className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white relative z-10">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
              </svg>
              Member since {data.joined_on}
            </span>
          )}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-white/95 underline underline-offset-2 hover:text-white disabled:opacity-60 relative z-10 block mx-auto"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12V4m0 0L8 8m4-4l4 4" />
            </svg>
            {uploading ? "Uploading…" : img ? "Change photo" : "Upload photo"}
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading profile…</div>
        ) : (
          <div className="p-5 sm:p-7">
            {/* Mobile compact profile head (desktop uses the gradient banner). */}
            <div className="sm:hidden mb-5 flex flex-col items-center text-center">
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl bg-gray-100 ring-1 ring-gray-200 overflow-hidden flex items-center justify-center text-2xl font-bold" style={{ color: primary }}>
                  {img ? <img src={img} alt="avatar" className="w-full h-full object-cover" /> : initial}
                </div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  aria-label="Change photo"
                  className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full bg-white shadow-md border flex items-center justify-center hover:bg-gray-50 disabled:opacity-60"
                  style={{ color: primary }}
                >
                  {uploading ? <Spinner size={14} /> : (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M15 8h.01M4 16l4-4a3 3 0 014 0l4 4M14 14l1-1a3 3 0 014 0l1 1M4 6h4l1.5-2h5L16 6h4a1 1 0 011 1v11a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1z" /></svg>
                  )}
                </button>
              </div>
              <h3 className="mt-3 text-lg font-bold text-gray-900">{name || member?.name}</h3>
              <p className="text-sm text-gray-500">{data?.email || member?.email}</p>
              {data?.joined_on && <p className="mt-1 text-xs text-gray-400">Member since {data.joined_on}</p>}
            </div>

            {/* Edit form */}
            <form onSubmit={save} className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-semibold tracking-[0.12em] text-gray-400 uppercase">Profile details</span>
                <span className="flex-1 h-px bg-gray-100" />
              </div>

              {/* Name + Phone — side by side on larger screens */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Name" value={name} onChange={setName} required disabled={saving} primary={primary} icon="user" />
                <PhoneField value={phone} onChange={setPhone} disabled={saving} primary={primary} />
              </div>

              {/* About */}
              <label className="block">
                <span className="text-[13px] font-medium text-gray-600 mb-1.5 flex items-center gap-1">
                  About
                  <span className="text-gray-400 font-normal">(optional)</span>
                </span>
                <textarea
                  value={about}
                  rows={3}
                  maxLength={1000}
                  disabled={saving}
                  placeholder="Tell the community a bit about yourself…"
                  onChange={(e) => setAbout(e.target.value)}
                  onFocus={(e) => {
                    e.target.style.borderColor = primary;
                    e.target.style.boxShadow = `0 0 0 3px ${primary}22`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                  className="w-full rounded-xl border px-3.5 py-2.5 text-sm text-gray-800 outline-none resize-none"
                  style={{ borderColor: "#e5e7eb" }}
                />
              </label>

              {/* Location search + preview map */}
              <LocationPicker token={token} primary={primary} value={loc} onChange={setLoc} />

              <button
                type="submit"
                disabled={saving}
                style={{
                  background: `linear-gradient(135deg, ${primary} 0%, ${shade(primary, -18)} 100%)`,
                  boxShadow: `0 8px 20px -8px ${primary}99`,
                }}
                className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3.5 rounded-xl hover:opacity-95 active:scale-[0.99] transition disabled:opacity-60 mt-1"
              >
                {saving && (
                  <Spinner size={16} />
                )}
                {saving ? "Saving…" : "Save changes"}
              </button>
            </form>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

const FIELD_ICONS = {
  user: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  phone: "M3 5a2 2 0 012-2h2.6a1 1 0 01.97.757l1.1 4.4a1 1 0 01-.27.95l-1.7 1.7a13 13 0 006.2 6.2l1.7-1.7a1 1 0 01.95-.27l4.4 1.1a1 1 0 01.76.97V19a2 2 0 01-2 2h-1C9.7 21 3 14.3 3 6V5z",
};

function Field({ label, value, onChange, required, optional, disabled, primary, icon }) {
  const [focused, setFocused] = useState(false);
  const iconPath = icon && FIELD_ICONS[icon];
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-gray-600 mb-1.5 flex items-center gap-1">
        {label}
        {optional && <span className="text-gray-400 font-normal">(optional)</span>}
      </span>
      <div className="relative">
        {iconPath && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors" style={focused ? { color: primary } : undefined}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
            </svg>
          </span>
        )}
        <input
          type="text"
          value={value}
          required={required}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-xl border ${iconPath ? "pl-9" : "pl-3.5"} pr-3.5 py-2.5 text-sm text-gray-800 outline-none`}
          style={{
            borderColor: focused ? primary : "#e5e7eb",
            boxShadow: focused ? `0 0 0 3px ${primary}22` : "none",
          }}
        />
      </div>
    </label>
  );
}

function shade(hex, percent) {
  try {
    let h = (hex || "").replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length !== 6) return hex;
    const num = parseInt(h, 16);
    const amt = Math.round(2.55 * percent);
    const r = Math.max(0, Math.min(255, (num >> 16) + amt));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amt));
    const b = Math.max(0, Math.min(255, (num & 0xff) + amt));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  } catch {
    return hex;
  }
}

export default ProfileModal;
